import { useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Alert, AlertTitle, AlertDescription } from '../components/ui/alert';
import { Loader2, X, AlertCircle } from 'lucide-react';
import { useNowPlaying } from '../contexts/NowPlayingContext';
import { useReadingPreferences } from '../contexts/ReadingPreferencesContext';
import { useCometTail } from '../hooks/useCometTail';

/**
 * NowPlayingPage — full-screen bimodal text reader.
 *
 * Renders the current chapter as word spans, each styled by the comet-tail
 * engine (see hooks/useCometTail.js). Audio element + media controls live
 * globally in App.jsx and persist across navigation.
 */
export default function NowPlayingPage() {
  const navigate = useNavigate();
  const {
    manifest,
    loading,
    error,
    bookId,
    currentChapter,
    currentChapterIndex,
    currentChapterWords,
    currentWordIndex,
    currentTime,
    clearBook,
  } = useNowPlaying();
  const { leadLetters, dyslexiaMode } = useReadingPreferences();

  const wordRefs = useRef([]);
  const containerRef = useRef(null);

  // Reset word refs when chapter changes.
  useEffect(() => {
    wordRefs.current = [];
  }, [currentChapterIndex]);

  // Convert the global audio word index to chapter-local.
  const chapterLocalWordIndex = useMemo(() => {
    if (!currentChapter || currentWordIndex < 0) return -1;
    return currentWordIndex - currentChapter.wordIndex;
  }, [currentChapter, currentWordIndex]);

  // Auto-scroll to the current audio word (not the read word — keeps the
  // audio-paced position visible even if the reader looks ahead).
  useEffect(() => {
    if (chapterLocalWordIndex >= 0 && wordRefs.current[chapterLocalWordIndex]) {
      wordRefs.current[chapterLocalWordIndex].scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [chapterLocalWordIndex]);

  // Redirect out if no book is loaded.
  useEffect(() => {
    if (!bookId && !loading) navigate('/my-library');
  }, [bookId, loading, navigate]);

  // Comet-tail coloring (read point, caps, anticipation, short-word extend).
  const { colorForWord, wordTransition } = useCometTail({
    chapterLocalWordIndex,
    currentChapterWords,
    currentTime,
    leadLetters,
  });

  // Render the chapter as paragraphs of colored word spans.
  const renderedContent = useMemo(() => {
    if (currentChapterWords.length === 0) return null;

    const paragraphs = [];
    let currentPara = [];
    let currentParaIndex = currentChapterWords[0].paragraphIndex;

    currentChapterWords.forEach((word, wordIdx) => {
      if (word.paragraphIndex !== currentParaIndex) {
        paragraphs.push(
          <p key={`para-${currentParaIndex}`} className="mb-6 text-lg leading-relaxed">
            {currentPara}
          </p>
        );
        currentPara = [];
        currentParaIndex = word.paragraphIndex;
      }

      currentPara.push(
        <span
          key={`word-${wordIdx}`}
          ref={el => wordRefs.current[wordIdx] = el}
          style={{ color: colorForWord(wordIdx), transition: wordTransition }}
        >
          {word.text}{' '}
        </span>
      );
    });

    if (currentPara.length > 0) {
      paragraphs.push(
        <p key={`para-${currentParaIndex}`} className="mb-6 text-lg leading-relaxed">
          {currentPara}
        </p>
      );
    }

    return paragraphs;
  }, [currentChapterWords, colorForWord, wordTransition]);

  const handleClose = () => {
    clearBook();
    navigate('/my-library');
  };

  if (loading || !manifest) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        <Card>
          <CardContent className="p-8">
            <div className="flex flex-col items-center justify-center space-y-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-lg text-muted-foreground">Loading book...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error Loading Book</AlertTitle>
          <AlertDescription className="mt-2">
            {error}
            <Button onClick={handleClose} variant="outline" className="mt-4 w-full">
              Back to Library
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // TODO(Reading Ruler): in dyslexia mode, dim all paragraphs except the one
  // containing the current word to 30% opacity so line-above/below crowding
  // doesn't pull the eye off-track. Deferred; requires tracking paragraphIndex
  // of the active word and walking <p> children.

  return (
    <div
      ref={containerRef}
      className={`max-w-[900px] mx-auto w-full px-4 sm:px-6 lg:px-8 py-6 pb-32 ${dyslexiaMode ? 'readwell-dyslexia-font' : ''}`}
    >
      <div className="flex justify-end gap-2 mb-4">
        <Button
          variant="ghost"
          size="icon"
          className="h-10 w-10"
          onClick={handleClose}
          aria-label="Close player"
        >
          <X className="h-5 w-5" />
        </Button>
      </div>

      {manifest?.status === 'generating' && (
        <Alert className="mb-4">
          <Loader2 className="h-4 w-4 animate-spin" />
          <AlertTitle>Generating</AlertTitle>
          <AlertDescription>
            {manifest.chunksReady} of {manifest.totalChunkCount} sections ready.
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardContent className="p-8">
          {currentChapter && (
            <h2 className="text-2xl font-bold font-serif mb-6">
              {currentChapter.title}
            </h2>
          )}

          {/* aria-live intentionally NOT on this container — color updates on
           * every word would flood screen readers. Player status announcements
           * live in the global status region in App.jsx. */}
          <div className="prose prose-lg max-w-none" role="article">
            {renderedContent}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
